import { useCallback, useEffect, useRef, useState } from "react";
import {
  deletePayrollOpeningBalance,
  listPayrollOpeningBalances,
  savePayrollOpeningBalance,
} from "./actions";

/**
 * usePayrollOpeningBalances - a business's PPh 21 opening balances for a year.
 *
 * `save` and `remove` throw, so the form that called them can show the
 * server's message where the user is looking; the list updates from the
 * Lambda's answer rather than a re-read.
 */
export const usePayrollOpeningBalances = (businessID, year) => {
  const [balances, setBalances] = useState([]);
  const [loading, setLoading] = useState(Boolean(businessID && year));
  const [error, setError] = useState(null);
  const readSeq = useRef(0);

  const refetch = useCallback(async () => {
    if (!businessID || !year) {
      setBalances([]);
      setLoading(false);
      return;
    }
    const seq = ++readSeq.current;
    setLoading(true);
    setError(null);
    try {
      const items = await listPayrollOpeningBalances({ businessID, year });
      if (seq !== readSeq.current) return;
      setBalances(items);
    } catch (err) {
      if (seq !== readSeq.current) return;
      setBalances([]);
      setError(err);
    } finally {
      if (seq === readSeq.current) setLoading(false);
    }
  }, [businessID, year]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const save = useCallback(
    async (payload) => {
      const record = await savePayrollOpeningBalance(payload);
      if (record.year === year) {
        setBalances((prev) => [
          ...prev.filter((item) => item.id !== record.id),
          record,
        ]);
      }
      return record;
    },
    [year],
  );

  const remove = useCallback(async (id) => {
    await deletePayrollOpeningBalance({ id });
    setBalances((prev) => prev.filter((item) => item.id !== id));
  }, []);

  return { balances, loading, error, refetch, save, remove };
};
